import React, { useRef, useState, useEffect } from "react";
import { highlightsSlides } from "../constants";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/all";
gsap.registerPlugin(ScrollTrigger);
import { pauseImg, playImg, replayImg } from "../utils";

const VideoCarousel = () => {
    const videoRef = useRef<HTMLVideoElement[]>([]);
    const videoSpanRef = useRef<HTMLSpanElement[]>([]);
    const videoDivRef = useRef<HTMLSpanElement[]>([]);

    interface VideoState {
        isEnd: boolean;
        startPlay: boolean;
        videoId: number;
        isLastVideo: boolean;
        isPlaying: boolean;
    }

    const [video, setVideo] = useState<VideoState>({
        isEnd: false,
        startPlay: false,
        videoId: 0,
        isLastVideo: false,
        isPlaying: false,
    });

    const [loadedData, setLoadedData] = useState<
        React.SyntheticEvent<HTMLVideoElement>[]
    >([]);

    const { isEnd, startPlay, videoId, isLastVideo, isPlaying } = video;

    useGSAP(() => {
        gsap.to("#slider", {
            transform: `translateX(${-100 * videoId}%)`,
            duration: 2,
            ease: "power2.inOut",
        });

        gsap.to("#video", {
            scrollTrigger: {
                trigger: "#video",
                toggleActions: "restart none none none",
            },
            onComplete: () => {
                setVideo((prevVideo) => ({
                    ...prevVideo,
                    startPlay: true,
                    isPlaying: true,
                }));
            },
        });
    }, [isEnd, videoId]);

    useEffect(() => {
        if (loadedData.length > 3) {
            if (!isPlaying) {
                videoRef.current[videoId].pause();
            } else {
                if (startPlay) videoRef.current[videoId].play();
            }
        }
    }, [startPlay, videoId, isPlaying, loadedData]);

    const handleLoadedMetadata = (
        i: number,
        e: React.SyntheticEvent<HTMLVideoElement>,
    ) => setLoadedData((pre) => [...pre, e]);

    useEffect(() => {
        let currentProgress = 0;
        const span = videoSpanRef.current;

        if (span[videoId]) {
            // animate video progress
            const anim = gsap.to(span[videoId], {
                onUpdate: () => {
                    const progress = Math.ceil(anim.progress() * 100);

                    if (progress !== currentProgress) {
                        currentProgress = progress;

                        videoDivRef.current[videoId].style.width =
                            window.innerWidth < 760
                                ? "10vw"
                                : window.innerWidth < 1200
                                  ? "10vw"
                                  : "4vw";

                        span[videoId].style.width = `${currentProgress}%`;
                        span[videoId].style.backgroundColor = "white";
                    }
                },
                onComplete: () => {
                    if (isPlaying) {
                        gsap.to(videoDivRef.current[videoId], {
                            width: "12px",
                        });
                        gsap.to(span[videoId], {
                            backgroundColor: "#afafaf",
                        });
                    }
                },
            });

            if (videoId === 0) {
                anim.restart();
            }

            const animUpdate = () => {
                anim.progress(
                    videoRef.current[videoId].currentTime /
                        highlightsSlides[videoId].videoDuration,
                );
            };

            if (isPlaying) {
                gsap.ticker.add(animUpdate);
            } else {
                gsap.ticker.remove(animUpdate);
            }
        }
    }, [videoId, startPlay]);

    const handleProcess = (
        type: "video-end" | "video-last" | "video-reset" | "play" | "pause",
        i: number = 0,
    ) => {
        switch (type) {
            case "video-end":
                setVideo((prevVideo) => ({
                    ...prevVideo,
                    isEnd: true,
                    videoId: i + 1,
                }));
                break;
            case "video-last":
                setVideo((prevVideo) => ({
                    ...prevVideo,
                    isLastVideo: true,
                }));
                break;
            case "video-reset":
                setVideo((prevVideo) => ({
                    ...prevVideo,
                    isLastVideo: false,
                    videoId: 0,
                }));
                break;
            case "play":
                setVideo((prevVideo) => ({
                    ...prevVideo,
                    isPlaying: !prevVideo.isPlaying,
                }));
                break;
            case "pause":
                setVideo((prevVideo) => ({
                    ...prevVideo,
                    isPlaying: !prevVideo.isPlaying,
                }));
                break;

            default:
                return video;
        }
    };

    return (
        <React.Fragment>
            <div className="flex items-center">
                {highlightsSlides.map((list, i) => (
                    <div key={list.id} id="slider" className="sm:pr-20 pr-10">
                        <div className="video-carousel_container">
                            <div className="w-full h-full flex-center rounded-3xl overflow-hidden bg-black">
                                <video
                                    id="video"
                                    playsInline={true}
                                    preload="metadata"
                                    muted
                                    className={`${list.id === 2 && "translate-x-44"} pointer-events-none`}
                                    ref={(el) => {
                                        if (el) videoRef.current[i] = el;
                                    }}
                                    onEnded={() =>
                                        i !== 3
                                            ? handleProcess("video-end", i)
                                            : handleProcess("video-last")
                                    }
                                    onPlay={() => {
                                        setVideo((prevVideo) => ({
                                            ...prevVideo,
                                            isPlaying: true,
                                        }));
                                    }}
                                    onLoadedMetadata={(e) =>
                                        handleLoadedMetadata(i, e)
                                    }
                                >
                                    <source src={list.video} type="video/mp4" />
                                </video>
                            </div>

                            <div className="absolute top-12 left-[5%] z-10">
                                {list.textLists.map((text) => (
                                    <p
                                        key={text}
                                        className="md:text-2xl text-xl font-medium"
                                    >
                                        {text}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="relative flex-center mt-10">
                <div className="flex-center py-5 px-7 bg-gray-300 backdrop-blur rounded-full">
                    {videoRef.current.map((_, i) => (
                        <span
                            key={i}
                            ref={(el) => {
                                if (el) videoDivRef.current[i] = el;
                            }}
                            className="mx-2 w-3 h-3 bg-gray-200 rounded-full relative"
                        >
                            <span
                                className="absolute h-full w-full rounded-full"
                                ref={(el) => {
                                    if (el) videoSpanRef.current[i] = el;
                                }}
                            />
                        </span>
                    ))}
                </div>
                <button className="control-btn">
                    <img
                        width={18}
                        height={18}
                        src={
                            isLastVideo
                                ? replayImg
                                : !isPlaying
                                  ? playImg
                                  : pauseImg
                        }
                        alt={
                            isLastVideo
                                ? "replay"
                                : !isPlaying
                                  ? "play"
                                  : "pause"
                        }
                        onClick={
                            isLastVideo
                                ? () => handleProcess("video-reset")
                                : !isPlaying
                                  ? () => handleProcess("play")
                                  : () => handleProcess("pause")
                        }
                    />
                </button>
            </div>
        </React.Fragment>
    );
};

export default VideoCarousel;
